import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { authenticatedNavigationItems } from '../config/navigation'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { reportsAPI, appealsAPI, leaderboardAPI, trainingGroupsAPI } from '../config/api'
import 'primeicons/primeicons.css'

interface Announcement {
  id: number
  title: string
  content: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  is_pinned: boolean
  image?: string
  created_by?: { username: string; name?: string }
  training_group?: { id: number; name: string } | null
  comment_count: number
  created_at: string
}

export default function HeaderAuthenticated() {
  const location = useLocation()
  const pathname = location.pathname
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [reportsCount, setReportsCount] = useState(0)
  const [maxStreak, setMaxStreak] = useState<number | null>(null)
  const [notifications, setNotifications] = useState<Announcement[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [readIds, setReadIds] = useState<Set<number>>(new Set())
  const [isNotiOpen, setIsNotiOpen] = useState(false)
  const notiRef = useRef<HTMLDivElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const { theme, toggleTheme } = useTheme()
  const { user, logout, isAuthenticated } = useAuth()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false)
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMobileMenuOpen])

  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsNotiOpen(false)
  }, [pathname])

  // Close notification dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notiRef.current && !notiRef.current.contains(e.target as Node)) {
        setIsNotiOpen(false)
      }
    }
    if (isNotiOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isNotiOpen])

  // Fetch announcements
  useEffect(() => {
    if (!isAuthenticated) return
    const fetchNotifications = async () => {
      try {
        const data = await trainingGroupsAPI.getAnnouncements()
        const arr: Announcement[] = Array.isArray(data) ? data : (data?.results || [])
        setNotifications(arr)
        // Lấy readIds từ localStorage
        const stored = localStorage.getItem('saigonfc_read_noti')
        const storedSet: Set<number> = stored ? new Set(JSON.parse(stored)) : new Set()
        setReadIds(storedSet)
        const unread = arr.filter(n => !storedSet.has(n.id)).length
        setUnreadCount(unread)
      } catch {
        setNotifications([])
      }
    }
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 60000)
    return () => clearInterval(interval)
  }, [isAuthenticated])

  const markAllRead = () => {
    const allIds = new Set(notifications.map(n => n.id))
    setReadIds(allIds)
    setUnreadCount(0)
    localStorage.setItem('saigonfc_read_noti', JSON.stringify([...allIds]))
  }

  const markOneRead = (id: number) => {
    const newSet = new Set(readIds)
    newSet.add(id)
    setReadIds(newSet)
    setUnreadCount(notifications.filter(n => !newSet.has(n.id)).length)
    localStorage.setItem('saigonfc_read_noti', JSON.stringify([...newSet]))
  }

  const priorityColor = (p: string) => {
    switch (p) {
      case 'urgent': return 'text-red-400 bg-red-500/10 border-red-500/30'
      case 'high':   return 'text-orange-400 bg-orange-500/10 border-orange-500/30'
      case 'normal': return 'text-gold-400 bg-gold-500/10 border-gold-500/30'
      default:       return 'text-gray-400 bg-gray-500/10 border-gray-500/30'
    }
  }

  const priorityLabel = (p: string) => {
    switch (p) {
      case 'urgent': return 'Khẩn cấp'
      case 'high':   return 'Cao'
      case 'normal': return 'Bình thường'
      default:       return 'Thấp'
    }
  }

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'Vừa xong'
    if (mins < 60) return `${mins} phút trước`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs} giờ trước`
    const days = Math.floor(hrs / 24)
    return `${days} ngày trước`
  }

  // Fetch max_streak từ API
  useEffect(() => {
    if (isAuthenticated && user) {
      const fetchMaxStreak = async () => {
        try {
          const ranking = await leaderboardAPI.getMyRanking()
          setMaxStreak(ranking?.max_streak || null)
        } catch (error) {
          // Ignore errors, fallback to user.streak
          setMaxStreak(null)
        }
      }
      fetchMaxStreak()
    }
  }, [isAuthenticated, user])

  // Fetch số lượng báo cáo chưa xử lý
  useEffect(() => {
    if (isAuthenticated && user) {
      const fetchReports = async () => {
        try {
          const [reports, appeals] = await Promise.all([
            reportsAPI.getMyReportsReceived().catch(() => []),
            appealsAPI.getAppeals().catch(() => [])
          ])

          // Đảm bảo là array
          const reportsArray = Array.isArray(reports) ? reports : []
          const appealsArray = Array.isArray(appeals) ? appeals : []

          // Đếm số báo cáo chưa có kháng cáo và có thể kháng cáo (status = resolved)
          // hoặc đang chờ xử lý (pending, investigating)
          const unresolvedCount = reportsArray.filter((r: any) => {
            // Kiểm tra xem đã có kháng cáo chưa
            const hasAppeal = appealsArray.some((a: any) =>
              a.report === r.id || a.report?.id === r.id
            )

            // Đếm các báo cáo:
            // 1. Chưa có kháng cáo
            // 2. Status là resolved (có thể kháng cáo) hoặc pending/investigating (đang chờ xử lý)
            return !hasAppeal && (
              r.status === 'resolved' ||
              r.status === 'pending' ||
              r.status === 'investigating'
            )
          }).length

          setReportsCount(unresolvedCount)
        } catch (error) {
          // Ignore errors
          setReportsCount(0)
        }
      }
      fetchReports()
      // Refresh mỗi 30 giây
      const interval = setInterval(fetchReports, 30000)
      return () => clearInterval(interval)
    }
  }, [isAuthenticated, user])

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${theme === 'dark'
        ? isScrolled
          ? 'bg-black/95 backdrop-blur-md border-b-2 border-gold-500 shadow-lg shadow-gold-500/20'
          : 'bg-black border-b-2 border-gold-500'
        : isScrolled
          ? 'bg-gray-200/95 backdrop-blur-md border-b-2 border-gold-500 shadow-lg shadow-gold-500/20'
          : 'bg-gray-200 border-b-2 border-gold-500'
        }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo Section */}
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-3 md:gap-4 hover:opacity-80 transition-all duration-300 group flex-shrink-0"
          >
            <div className="relative flex-shrink-0">
              <img
                src="/images/logo.png"
                alt="SAIGON FC Logo"
                width={isScrolled ? 45 : 50}
                height={isScrolled ? 45 : 50}
                className="object-contain transition-all duration-300"
              />
            </div>
            <div className="flex flex-col leading-tight min-w-0 items-start">
              <span
                className={`font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'} transition-all duration-300 whitespace-nowrap ${isScrolled ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'
                  }`}
              >
                SAIGON FC
              </span>
              <span
                className={`font-semibold transition-all duration-300 whitespace-nowrap text-[10px] sm:text-xs ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
              >
                Training League
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-1 justify-end max-w-[60%]">
            <nav className="flex items-center gap-1 xl:gap-1.5">
              {authenticatedNavigationItems.map((item) => {
                const isActive = pathname === item.href
                const isReportsPage = item.href === '/reports-appeals'
                return (
                  <div key={item.href} className="flex-shrink-0">
                    <Link
                      to={item.href}
                      className={`relative flex items-center justify-center gap-1 px-2.5 xl:px-3 py-2 rounded-lg transition-all duration-300 group ${isActive
                        ? 'bg-gold-gradient text-white font-bold'
                        : theme === 'dark'
                          ? 'text-white hover:text-gold-400'
                          : 'text-gray-700 hover:text-gray-900'
                        }`}
                    >
                      {item.icon && (
                        <i
                          className={`${item.icon} text-xs xl:text-sm transition-transform duration-300 group-hover:scale-110 hidden xl:inline-block`}
                        ></i>
                      )}
                      <span className="font-semibold text-[10px] xl:text-xs whitespace-nowrap">
                        {item.name}
                      </span>
                      {isReportsPage && reportsCount > 0 && (
                        <span className={`absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold ${theme === 'dark' ? 'bg-red-500 text-white' : 'bg-red-500 text-white'
                          }`}>
                          {reportsCount}
                        </span>
                      )}
                    </Link>
                  </div>
                )
              })}
            </nav>

            {/* Theme Toggle & Auth Buttons Desktop */}
            <div className="flex items-center gap-1.5 xl:gap-2 ml-2 xl:ml-3 pl-2 xl:pl-3 border-l border-gold-500/30 flex-shrink-0">
              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className={`w-10 h-10 flex items-center justify-center transition-all duration-300 rounded-lg active:scale-95 ${theme === 'dark'
                  ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
                  }`}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <i className="pi pi-sun text-lg"></i>
                ) : (
                  <i className="pi pi-moon text-lg"></i>
                )}
              </button>

              {/* 🔔 Notification Bell */}
              <div className="relative" ref={notiRef}>
                <button
                  onClick={() => { setIsNotiOpen(v => !v); if (!isNotiOpen && unreadCount > 0) {} }}
                  className={`relative w-10 h-10 flex items-center justify-center transition-all duration-300 rounded-lg active:scale-95 ${
                    theme === 'dark' ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20' : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
                  }`}
                  aria-label="Thông báo"
                >
                  <i className={`pi pi-bell text-lg ${isNotiOpen ? 'animate-bounce' : ''}`}></i>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {isNotiOpen && (
                  <div className={`absolute right-0 mt-2 w-96 max-h-[520px] rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden ${
                    theme === 'dark' ? 'bg-gray-950 border border-gold-500/30' : 'bg-white border border-gold-400/30'
                  }`}>
                    {/* Header */}
                    <div className={`flex items-center justify-between px-4 py-3 border-b ${
                      theme === 'dark' ? 'border-gold-500/20' : 'border-gold-400/20'
                    }`}>
                      <div className="flex items-center gap-2">
                        <i className="pi pi-bell text-gold-400 text-lg"></i>
                        <span className={`font-bold text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Thông báo</span>
                        {unreadCount > 0 && (
                          <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">{unreadCount} mới</span>
                        )}
                      </div>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllRead}
                          className={`text-xs font-semibold transition ${
                            theme === 'dark' ? 'text-gold-400 hover:text-gold-300' : 'text-gold-600 hover:text-gold-700'
                          }`}
                        >
                          Đánh dấu đã đọc
                        </button>
                      )}
                    </div>

                    {/* List */}
                    <div className="flex-1 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 gap-3">
                          <i className={`pi pi-bell text-4xl ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`}></i>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Không có thông báo nào</p>
                        </div>
                      ) : (
                        notifications.map(noti => {
                          const isRead = readIds.has(noti.id)
                          return (
                            <div
                              key={noti.id}
                              onClick={() => markOneRead(noti.id)}
                              className={`relative flex gap-3 px-4 py-3.5 cursor-pointer transition-all border-b ${
                                theme === 'dark' ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'
                              } ${!isRead ? (theme === 'dark' ? 'bg-gold-500/5' : 'bg-gold-50/60') : ''}`}
                            >
                              {/* Unread dot */}
                              {!isRead && (
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-gold-400"></span>
                              )}

                              {/* Icon */}
                              <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center border ${
                                priorityColor(noti.priority)
                              }`}>
                                {noti.is_pinned
                                  ? <i className="pi pi-thumbtack text-sm"></i>
                                  : noti.priority === 'urgent'
                                  ? <i className="pi pi-exclamation-triangle text-sm"></i>
                                  : noti.priority === 'high'
                                  ? <i className="pi pi-info-circle text-sm"></i>
                                  : <i className="pi pi-bell text-sm"></i>
                                }
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-1 mb-0.5">
                                  <p className={`text-sm font-semibold leading-snug line-clamp-1 ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                  } ${!isRead ? 'font-bold' : ''}`}>
                                    {noti.is_pinned && <span className="mr-1">📌</span>}
                                    {noti.title}
                                  </p>
                                </div>
                                <p className={`text-xs line-clamp-2 leading-relaxed mb-1 ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  {noti.content}
                                </p>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`text-[10px] font-medium ${
                                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                  }`}>
                                    {formatTimeAgo(noti.created_at)}
                                  </span>
                                  {noti.training_group && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                                      theme === 'dark' ? 'border-gold-500/30 text-gold-400 bg-gold-500/10' : 'border-gold-400/30 text-gold-600 bg-gold-50'
                                    }`}>
                                      {noti.training_group.name}
                                    </span>
                                  )}
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                                    priorityColor(noti.priority)
                                  }`}>
                                    {priorityLabel(noti.priority)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )
                        })
                      )}
                    </div>

                    {/* Footer */}
                    <div className={`px-4 py-2.5 border-t text-center ${
                      theme === 'dark' ? 'border-gold-500/20' : 'border-gold-400/20'
                    }`}>
                      <Link
                        to="/training_groups"
                        onClick={() => setIsNotiOpen(false)}
                        className={`text-xs font-semibold transition ${
                          theme === 'dark' ? 'text-gold-400 hover:text-gold-300' : 'text-gold-600 hover:text-gold-700'
                        }`}
                      >
                        Xem tất cả thông báo →
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* User Avatar Dropdown */}
              {user && (
                <div className="flex items-center gap-3">
                  <div className="relative group">
                    <div className={`relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${theme === 'dark' ? 'bg-gold-500/20 border-2 border-gold-500/50' : 'bg-gold-400/20 border-2 border-gold-400/50'} cursor-pointer hover:border-gold-400 transition`}>
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt={user.name || user.email}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <span className={`text-lg font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                          {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    {/* Dropdown Menu */}
                    <div className="absolute right-0 mt-2 w-80 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                      <div className={`${theme === 'dark' ? 'bg-black/95 border-2 border-gold-500/30' : 'bg-white border-2 border-gold-400/30'} rounded-xl overflow-hidden backdrop-blur-md`}>
                        {/* User Profile Section */}
                        <div className={`p-4 border-b ${theme === 'dark' ? 'border-gold-500/30' : 'border-gold-400/30'}`}>
                          <div className="flex items-center gap-3">
                            <div className={`relative w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${theme === 'dark' ? 'bg-gold-500/30' : 'bg-gold-400/30'}`}>
                              {user.avatar ? (
                                <img
                                  src={user.avatar}
                                  alt={user.name || user.email}
                                  className="w-full h-full object-cover rounded-full"
                                />
                              ) : (
                                <span className={`text-xl font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                                  {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-bold text-base break-words ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} title={user.email || user.name}>
                                {user.email || user.name}
                              </p>
                              <Link
                                to="/profile"
                                className={`text-xs ${theme === 'dark' ? 'text-gray-400 hover:text-gold-400' : 'text-gray-600 hover:text-gold-500'} transition`}
                              >
                                Xem hồ sơ
                              </Link>
                            </div>
                          </div>
                        </div>

                        {/* Check-in Button */}
                        <div className="p-4">
                          <Link
                            to="/checkin"
                            className={`w-full py-3 px-4 bg-gold-gradient text-white font-bold text-sm rounded-lg hover:shadow-lg hover:shadow-gold-500/50 transition transform hover:scale-105 flex items-center justify-center gap-2`}
                          >
                            <i className="pi pi-calendar text-lg"></i>
                            <span>ĐIỂM DANH HÔM NAY</span>
                          </Link>
                        </div>

                        {/* Statistics Cards */}
                        <div className="px-4 pb-4 grid grid-cols-3 gap-2">
                          {/* Streak */}
                          <div className={`rounded-lg p-3 text-center ${theme === 'dark' ? 'bg-black/50 border border-gold-500/20' : 'bg-gray-100 border border-gold-400/20'}`}>
                            <i className={`pi pi-bolt text-xl mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}></i>
                            <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{maxStreak !== null ? maxStreak : (user.streak || 0)}</p>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Chuỗi max</p>
                          </div>

                          {/* Points */}
                          <div className={`rounded-lg p-3 text-center ${theme === 'dark' ? 'bg-black/50 border border-gold-500/20' : 'bg-gray-100 border border-gold-400/20'}`}>
                            <i className={`pi pi-trophy text-xl mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}></i>
                            <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{(user as any)?.total_points || user.points || 0}</p>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Điểm</p>
                          </div>

                          {/* Days Remaining */}
                          <div className={`rounded-lg p-3 text-center ${theme === 'dark' ? 'bg-black/50 border border-gold-500/20' : 'bg-gray-100 border border-gold-400/20'}`}>
                            <i className={`pi pi-calendar text-xl mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}></i>
                            <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.daysRemaining || 0}</p>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Ngày còn</p>
                          </div>
                        </div>

                        {/* Logout Button */}
                        <div className={`p-4 border-t ${theme === 'dark' ? 'border-gold-500/30' : 'border-gold-400/30'}`}>
                          <button
                            onClick={() => {
                              logout()
                              navigate('/')
                            }}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition ${theme === 'dark' ? 'text-red-400 hover:bg-red-900/20 hover:text-red-300' : 'text-red-500 hover:bg-red-100 hover:text-red-600'}`}
                          >
                            <i className="pi pi-sign-out text-lg"></i>
                            <span className="font-semibold">Đăng Xuất</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Bell + Menu Buttons */}
          <div className="lg:hidden flex items-center gap-1">
            {/* 🔔 Mobile Notification Bell */}
            <div className="relative" ref={notiRef}>
              <button
                onClick={() => setIsNotiOpen(v => !v)}
                className={`relative w-10 h-10 flex items-center justify-center transition-all duration-300 rounded-lg active:scale-95 ${
                  theme === 'dark' ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20' : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
                }`}
                aria-label="Thông báo"
              >
                <i className="pi pi-bell text-lg"></i>
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Mobile Notification - Full Screen Panel like Mobile Menu */}
              {isNotiOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className={`fixed top-16 inset-x-0 bottom-0 z-[44] ${theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/50'}`}
                    onClick={() => setIsNotiOpen(false)}
                  />

                  {/* Short Sheet Panel */}
                  <nav className={`fixed top-16 left-0 right-0 max-h-[70vh] overflow-y-auto border-b-2 border-gold-500 shadow-2xl z-[45] ${
                    theme === 'dark' ? 'bg-black' : 'bg-gray-200'
                  }`}>
                    <div className="container mx-auto px-4 py-4">

                      {/* Header with Logo and Close */}
                      <div className="flex items-center justify-between h-14 mb-4 pb-3 border-b border-gold-500/30">
                        <div className="flex items-center gap-2">
                          <i className="pi pi-bell text-gold-400 text-xl"></i>
                          <span className={`font-bold text-lg ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                            Thông báo
                          </span>
                          {unreadCount > 0 && (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">{unreadCount} mới</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {unreadCount > 0 && (
                            <button
                              onClick={markAllRead}
                              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition ${
                                theme === 'dark' ? 'border-gold-500/40 text-gold-400 hover:bg-gold-900/20' : 'border-gold-400/40 text-gold-600 hover:bg-gold-50'
                              }`}
                            >
                              Đánh dấu đã đọc
                            </button>
                          )}
                          <button
                            onClick={() => setIsNotiOpen(false)}
                            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-300 active:scale-95 ${
                              theme === 'dark' ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20' : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
                            }`}
                            aria-label="Đóng"
                          >
                            <span className="text-xl font-bold">×</span>
                          </button>
                        </div>
                      </div>

                      {/* Notification List */}
                      <div className="flex flex-col gap-2 pb-4">
                        {notifications.length === 0 ? (
                          <div className={`flex flex-col items-center justify-center py-20 gap-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-gold-500/10' : 'bg-gold-100'}`}>
                              <i className="pi pi-bell text-4xl text-gold-400"></i>
                            </div>
                            <p className="text-lg font-semibold">Không có thông báo nào</p>
                          </div>
                        ) : (
                          notifications.map(noti => {
                            const isRead = readIds.has(noti.id)
                            return (
                              <div
                                key={noti.id}
                                onClick={() => markOneRead(noti.id)}
                                className={`relative flex gap-3 py-3.5 px-4 rounded-lg border-l-4 transition-all duration-300 cursor-pointer ${
                                  !isRead
                                    ? theme === 'dark'
                                      ? 'border-gold-400 bg-gold-900/20 hover:bg-gold-900/30'
                                      : 'border-gold-500 bg-gold-400/15 hover:bg-gold-400/25'
                                    : theme === 'dark'
                                    ? 'border-transparent hover:border-gold-500 hover:bg-gold-900/20'
                                    : 'border-transparent hover:border-gold-400 hover:bg-gold-400/10'
                                }`}
                              >
                                {/* Icon */}
                                <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center border ${priorityColor(noti.priority)}`}>
                                  {noti.is_pinned
                                    ? <i className="pi pi-thumbtack text-sm"></i>
                                    : noti.priority === 'urgent'
                                    ? <i className="pi pi-exclamation-triangle text-sm"></i>
                                    : noti.priority === 'high'
                                    ? <i className="pi pi-info-circle text-sm"></i>
                                    : <i className="pi pi-bell text-sm"></i>
                                  }
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <p className={`text-base leading-snug mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'} ${!isRead ? 'font-bold' : 'font-semibold'}`}>
                                    {noti.is_pinned && <span className="mr-1">📌</span>}
                                    {noti.title}
                                  </p>
                                  <p className={`text-sm leading-relaxed mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {noti.content}
                                  </p>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                      {formatTimeAgo(noti.created_at)}
                                    </span>
                                    {noti.training_group && (
                                      <span className={`text-xs px-2 py-0.5 rounded-full border ${
                                        theme === 'dark' ? 'border-gold-500/30 text-gold-400 bg-gold-500/10' : 'border-gold-400/30 text-gold-600 bg-gold-50'
                                      }`}>
                                        {noti.training_group.name}
                                      </span>
                                    )}
                                    <span className={`text-xs px-2 py-0.5 rounded-full border ${priorityColor(noti.priority)}`}>
                                      {priorityLabel(noti.priority)}
                                    </span>
                                  </div>
                                </div>

                                {/* Unread dot */}
                                {!isRead && (
                                  <span className="w-2 h-2 rounded-full bg-gold-400 flex-shrink-0 mt-1"></span>
                                )}
                              </div>
                            )
                          })
                        )}

                        {/* Footer Link */}
                        {notifications.length > 0 && (
                          <div className={`flex items-center justify-center pt-3 mt-2 border-t border-gold-500/30`}>
                            <Link
                              to="/training_groups"
                              onClick={() => setIsNotiOpen(false)}
                              className={`w-full py-3.5 px-4 flex items-center justify-center gap-2.5 font-semibold text-base transition-all duration-300 active:scale-95 rounded-lg border-2 border-gold-500/40 ${
                                theme === 'dark' ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20' : 'text-gray-800 hover:text-gray-900 hover:bg-gold-400/20'
                              }`}
                            >
                              <i className="pi pi-bell text-lg"></i>
                              <span>Xem tất cả thông báo</span>
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </nav>
                </>
              )}
            </div>

            {/* Hamburger Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="relative w-10 h-10 flex items-center justify-center text-gold-400 hover:text-gold-300 transition-colors duration-300 rounded-lg hover:bg-gold-900/20 active:scale-95"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
            <div>
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className={`lg:hidden fixed inset-0 backdrop-blur-sm z-40 ${theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/50'
                }`}
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu */}
            <nav
              ref={mobileMenuRef}
              className={`lg:hidden fixed top-0 left-0 right-0 h-screen overflow-y-auto border-b-2 border-gold-500 shadow-xl z-50 ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'
                }`}
            >
              <div className="container mx-auto px-4 py-4">
                {/* Header with Logo and Close Button */}
                <div className="flex items-center justify-between h-14 mb-4 pb-3 border-b border-gold-500/30">
                  {/* Logo */}
                  <Link
                    to="/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 hover:opacity-80 transition-all duration-300 flex-shrink-0"
                  >
                    <div className="relative flex-shrink-0">
                      <img
                        src="/images/logo.png"
                        alt="SAIGON FC Logo"
                        width={40}
                        height={40}
                        className="object-contain"
                      />
                    </div>
                    <div className="flex flex-col leading-tight min-w-0 items-start">
                      <span className={`font-bold text-sm whitespace-nowrap ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                        SAIGON FC
                      </span>
                      <span className={`font-semibold text-[10px] whitespace-nowrap ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Training League
                      </span>
                    </div>
                  </Link>

                  {/* Close Button */}
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-300 active:scale-95 ${theme === 'dark'
                      ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
                      }`}
                    aria-label="Close menu"
                  >
                    <span className="text-xl font-bold">×</span>
                  </button>
                </div>

                <div className="flex flex-col gap-2 pb-4">
                  {authenticatedNavigationItems.map((item) => {
                    const isActive = pathname === item.href
                    const isReportsPage = item.href === '/reports-appeals'
                    return (
                      <div key={item.href}>
                        <Link
                          to={item.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`flex items-center gap-3 py-3.5 px-4 rounded-lg
                              border-l-4 transition-all duration-300 relative
                              ${theme === 'dark'
                              ? isActive
                                ? 'border-gold-400 text-gold-400 font-bold bg-gold-900/20'
                                : 'border-transparent text-white hover:border-gold-500 hover:text-gold-400 hover:bg-gold-900/20 active:bg-gold-900/30'
                              : isActive
                                ? 'border-gold-500 text-gold-600 font-bold bg-gold-400/15'
                                : 'border-transparent text-gray-800 hover:border-gold-500 hover:text-gray-900 hover:bg-gold-400/20 active:bg-gold-400/30'
                            }
                          `}>
                          {item.icon && (
                            <i
                              className={`${item.icon} text-xl transition-transform duration-300`}
                            />
                          )}

                          <span className="font-semibold text-base">{item.name}</span>

                          {isReportsPage && reportsCount > 0 && (
                            <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-bold ${theme === 'dark' ? 'bg-red-500 text-white' : 'bg-red-500 text-white'
                              }`}>
                              {reportsCount}
                            </span>
                          )}
                        </Link>
                      </div>

                    )
                  })}

                  {/* Theme Toggle Mobile */}
                  <div className="flex items-center justify-center pt-3 mt-2 border-t border-gold-500/30">
                    <button
                      onClick={toggleTheme}
                      className={`w-full py-3.5 px-4 flex items-center justify-center gap-2.5 font-semibold text-base transition-all duration-300 active:scale-95 rounded-lg border-2 border-gold-500/40 ${theme === 'dark'
                        ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20 active:bg-gold-900/30'
                        : 'text-gray-800 hover:text-gray-900 hover:bg-gold-400/20 active:bg-gold-400/30'
                        }`}
                      aria-label="Toggle theme"
                    >
                      {theme === 'dark' ? (
                        <>
                          <i className="pi pi-sun text-xl"></i>
                          <span>Chế độ sáng</span>
                        </>
                      ) : (
                        <>
                          <i className="pi pi-moon text-xl"></i>
                          <span>Chế độ tối</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* User Profile Mobile */}
                  {user && (
                    <div className="flex flex-col gap-3 pt-3 mt-2 border-t border-gold-500/30">
                      {/* User Profile Section */}
                      <div className={`px-4 py-3.5 rounded-lg border-2 border-gold-500/40 ${theme === 'dark' ? 'bg-gold-900/20' : 'bg-gold-400/20'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`relative w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden border-2 ${theme === 'dark' ? 'bg-gold-500/30 border-gold-500/50' : 'bg-gold-400/30 border-gold-400/50'}`}>
                            {user.avatar ? (
                              <img
                                src={user.avatar}
                                alt={user.name || user.email}
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              <span className={`text-xl font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                                {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 flex items-center justify-between min-w-0">
                            <div className="flex-1 min-w-0">
                              <p className={`font-bold text-sm break-words mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} title={user.email || user.name}>
                                {user.email || user.name}
                              </p>
                              <Link
                                to="/profile"
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`text-xs font-semibold ${theme === 'dark' ? 'text-gold-400 hover:text-gold-300' : 'text-gold-600 hover:text-gold-700'} transition`}
                              >
                                Xem hồ sơ →
                              </Link>
                            </div>
                            <button
                              onClick={() => {
                                logout()
                                setIsMobileMenuOpen(false)
                                navigate('/')
                              }}
                              className={`p-2.5 rounded-lg transition ${theme === 'dark'
                                ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20'
                                : 'text-red-500 hover:text-red-600 hover:bg-red-100'
                                }`}
                              aria-label="Logout"
                            >
                              <i className="pi pi-sign-out text-xl"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Check-in Button */}
                      <Link
                        to="/checkin"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`w-full py-4 px-4 bg-gold-gradient text-white font-bold text-base rounded-lg shadow-lg shadow-gold-500/30 transition transform active:scale-95 flex items-center justify-center gap-2.5`}
                      >
                        <i className="pi pi-calendar text-xl"></i>
                        <span>ĐIỂM DANH HÔM NAY</span>
                      </Link>

                      {/* Statistics Cards */}
                      <div className="grid grid-cols-3 gap-2.5">
                        {/* Streak */}
                        <div className={`rounded-lg p-3.5 text-center border-2 ${theme === 'dark' ? 'bg-black/60 border-gold-500/30' : 'bg-gray-100 border-gold-400/30'}`}>
                          <i className={`pi pi-bolt text-xl mb-1.5 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}></i>
                          <p className={`text-xl font-bold mb-0.5 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{maxStreak !== null ? maxStreak : (user.streak || 0)}</p>
                          <p className={`text-xs font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Chuỗi max</p>
                        </div>

                        {/* Points */}
                        <div className={`rounded-lg p-3.5 text-center border-2 ${theme === 'dark' ? 'bg-black/60 border-gold-500/30' : 'bg-gray-100 border-gold-400/30'}`}>
                          <i className={`pi pi-trophy text-xl mb-1.5 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}></i>
                          <p className={`text-xl font-bold mb-0.5 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{(user as any)?.total_points || user.points || 0}</p>
                          <p className={`text-xs font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Điểm</p>
                        </div>

                        {/* Days Remaining */}
                        <div className={`rounded-lg p-3.5 text-center border-2 ${theme === 'dark' ? 'bg-black/60 border-gold-500/30' : 'bg-gray-100 border-gold-400/30'}`}>
                          <i className={`pi pi-calendar text-xl mb-1.5 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}></i>
                          <p className={`text-xl font-bold mb-0.5 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.daysRemaining || 0}</p>
                          <p className={`text-xs font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Ngày còn</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </nav>
          </>
        )}
      </div>
    </header>
  )
}


